import { useState } from "react";
import { useRouter } from "expo-router";
import { StyleSheet, View } from "react-native";

import { UpgradePrompt } from "~/components/composed/UpgradePrompt";
import {
  AppModal,
  AppText,
  Badge,
  Button,
  ButtonText,
  Card,
  EmptyState,
  ListRow,
  MetricCard,
  ProtectedScreen,
  SectionHeader,
  Stack,
  TextField,
} from "~/components/ui";
import { usePlan } from "~/hooks/usePlan";
import { useCollectionActions, useCollections } from "~/state/collections";
import { usePlants } from "~/state/plants";
import { spacing } from "~/styles/theme";

export default function CollectionsScreen() {
  const router = useRouter();
  const plan = usePlan();
  const collections = useCollections();
  const plants = usePlants();
  const actions = useCollectionActions();
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mapped = collections.filter(
    (collection) => collection.map_kind !== "none",
  );

  function create() {
    if (!name.trim()) return;
    try {
      const id = actions.create({ name, description });
      setName("");
      setDescription("");
      setError(null);
      setCreateOpen(false);
      router.push(`/collections/${id}` as never);
    } catch (createError) {
      setError(
        createError instanceof Error
          ? createError.message
          : "Unable to create this collection.",
      );
    }
  }

  return (
    <ProtectedScreen
      title="Collections"
      subtitle="Group your plants however you think about them — by garden, by room, or by obsession."
    >
      <View style={stylesheet.metrics}>
        <View style={stylesheet.metric}>
          <MetricCard
            label="Collections"
            value={
              plan.collections.limit === null
                ? String(collections.length)
                : `${collections.length} / ${plan.collections.limit}`
            }
          />
        </View>
        <View style={stylesheet.metric}>
          <MetricCard
            label="Plants"
            value={
              plan.plants.limit === null
                ? String(plants.length)
                : `${plants.length} / ${plan.plants.limit}`
            }
          />
        </View>
        <View style={stylesheet.metric}>
          <MetricCard label="With a map" value={String(mapped.length)} />
        </View>
      </View>

      <Card gap="md">
        <SectionHeader
          title="Your collections"
          description="Everything is stored on this device first, then synced when you have a connection."
        />
        {collections.length === 0 ? (
          <EmptyState
            title="No collections yet"
            description="Create your first collection and start cataloguing what you grow."
          />
        ) : (
          <Stack gap="sm">
            {collections.map((collection) => {
              const count = plants.filter(
                (plant) => plant.collection_id === collection.id,
              ).length;
              return (
                <ListRow
                  key={collection.id}
                  title={collection.name}
                  description={collection.description ?? undefined}
                  onPress={() =>
                    router.push(`/collections/${collection.id}` as never)
                  }
                  meta={
                    <>
                      <Badge tone="neutral">
                        {count} {count === 1 ? "plant" : "plants"}
                      </Badge>
                      {collection.map_kind !== "none" ? (
                        <Badge tone="success">
                          {collection.map_kind === "geo"
                            ? "Mapped"
                            : "Custom map"}
                        </Badge>
                      ) : null}
                      {collection.visibility !== "private" ? (
                        <Badge tone="neutral">{collection.visibility}</Badge>
                      ) : null}
                    </>
                  }
                  trailing={
                    collection.map_kind !== "none" ? (
                      <Button
                        compact
                        variant="outline"
                        iconName="map"
                        onPress={() =>
                          router.push(
                            `/collections/${collection.id}/map` as never,
                          )
                        }
                      >
                        <ButtonText>Map</ButtonText>
                      </Button>
                    ) : undefined
                  }
                />
              );
            })}
          </Stack>
        )}

        {plan.collections.allowed ? (
          <Button
            align="start"
            iconName="plus"
            onPress={() => setCreateOpen(true)}
          >
            <ButtonText>New collection</ButtonText>
          </Button>
        ) : (
          <AppText size="xs">{plan.collections.message}</AppText>
        )}
      </Card>

      {!plan.collections.allowed ? (
        <UpgradePrompt title="Want more than one collection?" />
      ) : null}

      <AppModal
        title="New collection"
        visible={createOpen}
        onClose={() => setCreateOpen(false)}
      >
        <Stack gap="md">
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Name
            </AppText>
            <TextField
              accessibilityLabel="Collection name"
              value={name}
              placeholder="Front garden"
              onChangeText={setName}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Description (optional)
            </AppText>
            <TextField
              accessibilityLabel="Collection description"
              value={description}
              placeholder="Everything along the north fence"
              multiline
              style={stylesheet.textarea}
              onChangeText={setDescription}
            />
          </View>
          {error ? (
            <AppText size="xs" tone="danger">
              {error}
            </AppText>
          ) : null}
          <Button iconName="plus" disabled={!name.trim()} onPress={create}>
            <ButtonText>Create collection</ButtonText>
          </Button>
        </Stack>
      </AppModal>
    </ProtectedScreen>
  );
}

const stylesheet = StyleSheet.create({
  metrics: { flexDirection: "row", flexWrap: "wrap", gap: spacing.m },
  metric: { minWidth: 180, flex: 1 },
  field: { gap: spacing.xs },
  textarea: { minHeight: 86, textAlignVertical: "top" },
});
